import { infiniteQueryFetchNextPage } from '@repo/sdk'
import type {
	OperatorAuditEvent,
	V1BackofficeListAuditEventsData
} from '@repo/sdk/gen'
import { zV1BackofficeListAuditEventsData } from '@repo/sdk/zod'
import { DataTableColumnSettings } from '@repo/ui/components/data-table/column-settings'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { PageLayout } from '@repo/ui/components/page-layout'
import { dateToEndOfDayIso, dateToStartOfDayIso } from '@repo/ui/dates'
import { getErrorCode, raise, zodNumberMaxValue } from '@repo/ui/utils'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { OperatorBreadcrumb } from '@/routes/-components/operator-breadcrumb'
import {
	AuditActionCell,
	AuditAdminCell,
	AuditDetailCell,
	AuditOrganizationCell,
	AuditTimestampCell
} from '@/routes/(private)/audit/-components/audit-cells'
import { AuditEmpty } from '@/routes/(private)/audit/-components/audit-empty'
import { AuditFilters } from '@/routes/(private)/audit/-components/audit-filters'
import {
	auditSearchSchema,
	isAuditFiltered
} from '@/routes/(private)/audit/-lib/audit-search'
import { auditEventsQueryOptions } from '@/routes/(private)/audit/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

export const Route = createFileRoute('/(private)/audit/')({
	component: OperatorAudit,
	validateSearch: auditSearchSchema,
	staticData: {
		breadcrumb: 'operator-audit:page_title'
	}
})

/**
 * What the platform operator's own console did, across every organization on
 * the instance — the complement of each organization's own tenant trail
 * (`backend/src/modules/backoffice/frontend.md`: "not the same list at two
 * scopes").
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THIS PAGE OFFERS NO ACTIONS, THE SAME WAY THE CLIENT'S OWN AUDIT PAGE DOES
 * NOT: no export, no row menu, no selection. A row's organization name links
 * out because that is where the "why" behind an act lives, not because this
 * table does anything to it.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * **No count.** `list-audit-events` merges two tables with no cheap combined
 * total, unlike `list-organizations`, so there is nothing here to state next
 * to the column settings in the table's footer the way the organizations
 * list states "8 organizzazioni".
 *
 * Paging is the endpoint's cursor, consumed as an infinite query. A cursor
 * the server rejects — expired, or invalidated by filters that changed
 * underneath — is answered by restarting from the first page in silence,
 * which is what `cursor_invalid` asks for; an error toast would report a
 * routine event as a fault.
 */
function OperatorAudit() {
	const { t } = useTranslation()
	const params = Route.useSearch()
	const queryClient = useQueryClient()
	const pageSize = usePageSize((state) => state.pageSize)

	const listQuery = {
		limit: Math.min(pageSize, MAX_PAGE_SIZE),
		// The day a person picked, widened to the instants that bound it — the
		// boundary bug every date filter has once, avoided here as the
		// client's own audit page avoids it.
		from: dateToStartOfDayIso(params.from),
		to: dateToEndOfDayIso(params.to),
		actions: params.actions,
		organizationId: params.organizationId
	} satisfies Omit<
		NonNullable<V1BackofficeListAuditEventsData['query']>,
		'cursor'
	>

	const events = useInfiniteQuery(auditEventsQueryOptions(listQuery))
	const rows = events.data ?? EMPTY_EVENTS

	// Not an error to show. The cursor expired or the filters moved under it,
	// and the answer is another first page — which is what the load-more
	// handler asks the cache for.
	const cursorExpired = getErrorCode(events.error) === 'cursor_invalid'

	const columns: DataTableColumn<OperatorAuditEvent>[] = [
		{
			id: 'createdAt',
			header: t('operator-audit:columns.timestamp'),
			pinned: 'left',
			cell: (event) => <AuditTimestampCell event={event} />,
			width: 168
		},
		{
			id: 'admin',
			header: t('operator-audit:columns.admin'),
			cell: (event) => <AuditAdminCell event={event} />,
			width: 200
		},
		{
			id: 'action',
			header: t('operator-audit:columns.action'),
			cell: (event) => <AuditActionCell event={event} />,
			width: 240
		},
		{
			id: 'organization',
			header: t('operator-audit:columns.organization'),
			cell: (event) => <AuditOrganizationCell event={event} />,
			minWidth: 160,
			flex: 1
		},
		{
			id: 'detail',
			header: t('operator-audit:columns.detail'),
			cell: (event) => <AuditDetailCell event={event} />,
			width: 260
		}
	]

	const columnsState = useDataTableColumnState(columns)

	/**
	 * One more page, and the one refusal that is not a failure — see
	 * `organizationsListQueryOptions`'s own `loadNextPage` for the reasoning,
	 * which is identical here.
	 */
	async function loadNextPage() {
		const result = await infiniteQueryFetchNextPage(events)
		if (getErrorCode(result?.error) === 'cursor_invalid') {
			await queryClient.resetQueries({
				queryKey: makeQueryKeys.audit.index(listQuery)
			})
		}
	}

	return (
		<PageLayout bar={<AuditFilters />} breadcrumb={<OperatorBreadcrumb />}>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6'>
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={rows}
					footer={
						<DataTableColumnSettings
							columns={columns}
							columnsState={columnsState}
						/>
					}
					loadMore={{
						hasMore: events.hasNextPage,
						loading: events.isFetchingNextPage,
						onLoadMore: loadNextPage
					}}
					rowHeight={48}
					state={{
						empty: (
							<AuditEmpty filtered={isAuditFiltered(params)} />
						),
						error: events.isError && !cursorExpired,
						loading: events.isPending || events.isPlaceholderData
					}}
				/>
			</div>
		</PageLayout>
	)
}

const EMPTY_EVENTS: OperatorAuditEvent[] = []

/**
 * The endpoint's own ceiling on a page, off the contract rather than
 * retyped — the same reasoning `organizationsListQueryOptions`'s own
 * `MAX_PAGE_SIZE` carries, reconciled against the app-wide rows-per-page
 * preference the same way.
 */
const MAX_PAGE_SIZE =
	zodNumberMaxValue(
		zV1BackofficeListAuditEventsData.shape.query.unwrap().shape.limit
	) ?? raise('list-audit-events lost its page-size ceiling')
